import {
  PaymentCustomerPaymentMethods,
  PaymentCustomers,
  Plans,
  Subscriptions,
} from '@prisma/client';
import type { Stripe } from 'stripe';
import * as yup from 'yup';

export interface SubscriptionCard {
  type: 'card';
  last4: Stripe.PaymentMethod.Card['last4'];
  brand: Stripe.PaymentMethod.Card['brand'];
  exp: {
    month: Stripe.PaymentMethod.Card['exp_month'];
    year: Stripe.PaymentMethod.Card['exp_year'];
  };
}

export interface SubscriptionLink {
  type: 'link';
  email: string;
}

export interface SubscriptionUsBankAccount {
  type: 'us_bank_account';
  last4: Stripe.PaymentMethod.UsBankAccount['last4'];
  brand: Stripe.PaymentMethod.UsBankAccount['bank_name'];
}

export interface SubscriptionProduct {
  name: string;
  description: string;
  price: number; // in cents
}

export interface GetSubscriptionResponse {
  paymentCustomer:
    | (PaymentCustomers & {
        paymentMethods: PaymentCustomerPaymentMethods[];
      })
    | null;
  subscription:
    | (Subscriptions & {
        plan: Plans;
      })
    | null;
  isActive: boolean;
}

export interface GetStripeSubscriptionResponse {
  paymentMethod:
    | SubscriptionCard
    | SubscriptionLink
    | SubscriptionUsBankAccount
    | null;
  product: SubscriptionProduct | null;
  status: Stripe.Subscription.Status;
  renewsAt: number | null;
  testClock: Stripe.Response<Stripe.TestHelpers.TestClock> | null;
  coupon: Stripe.Coupon | null;
}

export interface CreatePortalSessionResponse {
  url: string;
}

export type CreatePortalSessionType =
  | 'subscription_cancel'
  | 'payment_method_update';

export interface CreateInvoiceHoistedUrlResponse {
  url: string;
}

export interface ResumeSubscriptionResponse {
  success: boolean;
}

export interface ResubscribeSubscriptionResponse {
  subscription: Subscriptions;
}

export interface SwitchToFreePlanResponse {
  subscription: Pick<Stripe.Subscription, 'id' | 'status'>;
}

export type SubscriptionModalAction = 'manage' | 'create_location';

export interface SubscriptionModalContent {
  header: string;
  title: string;
  description: string;
  positiveButtonText: string;
  negativeButtonText: string;
  action?: SubscriptionModalAction;
}

// Duplicate any changes in API directory subscriptions constants
export enum Feature {
  MANAGE_REQUESTS = 'MANAGE_REQUESTS',
  ADD_APPOINTMENT = 'ADD_APPOINTMENT',
  MANAGE_APPOINTMENT = 'MANAGE_APPOINTMENT',
  ADD_CUSTOMER = 'ADD_CUSTOMER',
  MANAGE_CUSTOMER = 'MANAGE_CUSTOMER',
  CHAT = 'CHAT',
  MANAGE_PROFILE = 'MANAGE_PROFILE',
  CHECKOUT = 'CHECKOUT',
  MANAGE_TEAM = 'MANAGE_TEAM',
  WAIVERS = 'WAIVERS',
  MARKETING = 'MARKETING',
  DISCOUNTS = 'DISCOUNTS',
  ITEM_LIBRARY = 'ITEM_LIBRARY',
  ADS = 'ADS',
}

export const updateSubscriptionPlanSchema = yup.object({
  planId: yup.number().integer().min(0).required(),
  businessId: yup.string().required(),
});

export type UpdateSubscriptionPlanPayload = yup.InferType<
  typeof updateSubscriptionPlanSchema
>;
